import { mapState } from "vuex";

/**
 * Flags evaluated against the SELECTED client (`globalClientId`) rather than the
 * signed-in user's own client.
 *
 * Use this in the admin screens, where an internal admin picks a client out of the
 * dropdown and configures it: there "is this feature on" means the client being
 * configured, and the app-wide `featureFlags` mixin would answer for the admin's own
 * client instead. Everywhere else, keep using `featureFlags`.
 *
 * The set is lazy. A component that needs it calls `loadClientFeatureFlags()` once —
 * typically from the same place it loads its other data — and the store keeps it in
 * step with the dropdown from then on.
 */
export default {
  methods: {
    /**
     * ADO 186742 "Engage-Inform User Synchronization and Merged User Creation".
     *
     * Off is not "feature disabled" — it selects the path a client is on. Off keeps
     * today's Inform sync behaviour; on moves that client to the merged-user flow.
     */
    isEngageUserSyncEnabled() {
      // Optional chaining, not a bare index: the set is lazy, so a component can read it
      // before the store holds it at all. Undefined must read off rather than throw —
      // off is "keep today's behaviour", which is the right answer while it loads.
      return !!this.clientFeatureFlags?.["feature-engage-users-sync-to-inform"];
    },
    /**
     * Whether the dynamic data program builder opens on the claim-program intro in create
     * mode. Pair it with `isClientFeatureFlagsResolvedFor`, since off and unresolved look alike.
     */
    isDynamicProgramBuilderClaimsEnabled() {
      return !!this.clientFeatureFlags?.["feature-dynamic-program-builder-claims"];
    },
    /**
     * Whether the set currently in the store was evaluated for `clientId`.
     *
     * Every flag defaults to false, so a bare read cannot separate "off for this
     * client" from "not resolved yet" or "resolved for the client that was selected
     * a moment ago". A screen whose LAYOUT depends on a flag has to make that
     * distinction — otherwise it renders the off-branch, then corrects itself, which
     * reads to an admin as the feature being missing.
     *
     * Compared as strings because the store holds the id it sent to flagd, which is
     * stringified for JSON Logic's strict-equality `in`, while a client object carries
     * the number.
     *
     * @param {number|string|null|undefined} clientId
     * @returns {boolean}
     */
    isClientFeatureFlagsResolvedFor(clientId) {
      console.log("isClientFeatureFlagsResolvedFor", clientId, this.clientFeatureFlagsClientId);
      if (clientId === null || clientId === undefined) return false;
      if (this.clientFeatureFlagsClientId === null) return false;
      return this.clientFeatureFlagsClientId === String(clientId);
    },
    /**
     * Open the client-scoped flag set and evaluate it for the current client.
     *
     * Opens a second flagd connection the first time it runs, which is why it is not
     * done at app start. Safe to call more than once.
     * @returns {Promise<void>}
     */
    async loadClientFeatureFlags() {
      await this.$store.dispatch("setClientFeatureFlags", { initialize: true });
    },
  },
  computed: {
    ...mapState(["clientFeatureFlags", "clientFeatureFlagsClientId"]),
  },
};
